import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { getConsumptionsSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';
import { ECHARTS_COLOR_PALETTE } from '../../../../../Components/Hooks/useColorArrayHooks';

const CostPPEPositionEChart = ({
    title,
    legendPosition = 'bottom',
    defaultFilters = {
        option: 'dia',
    },
    minHeight,
    maxHeight,
    companyId = null,
}) => {
    const dispatch = useDispatch();
    const [selectedDate, setSelectedDate] = useState(defaultFilters.option);
    const [selectedFuelType, setSelectedFuelType] = useState('diesel');

    const selectorConsumptions = createSelector(
        state => state.FuelSupply,
        data => ({ info: data.consumptionsSummaryManagement || null })
    );
    const { info: infoConsumptions } = useSelector(selectorConsumptions);

    const fetchConsumptionsData = useCallback(
        (filter, company_id) => {
            dispatch(
                getConsumptionsSummary({
                    // NO establishment_id here - this is for management view
                    filter: filter,
                    group: 'establecimiento', // Group by establishment
                    company_id: company_id, // Add company filter
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        fetchConsumptionsData(selectedDate, companyId);
    }, [selectedDate, companyId, fetchConsumptionsData]);

    // Extract unique fuel types from API response
    const availableFuelTypes = useMemo(() => {
        if (!infoConsumptions?.data || !Array.isArray(infoConsumptions.data))
            return [];

        const fuelTypesSet = new Set();
        infoConsumptions.data.forEach(item => {
            if (item.establishments && Array.isArray(item.establishments)) {
                item.establishments.forEach(est => {
                    if (est.fuel_types && Array.isArray(est.fuel_types)) {
                        est.fuel_types.forEach(fuel => {
                            if (fuel.name) {
                                fuelTypesSet.add(fuel.name.toLowerCase());
                            }
                        });
                    }
                });
            }
        });

        return Array.from(fuelTypesSet);
    }, [infoConsumptions?.data]);

    // Set default fuel type to 'diesel' when available fuel types change
    useEffect(() => {
        if (availableFuelTypes.length > 0) {
            // Check if current selected fuel type is available
            if (!availableFuelTypes.includes(selectedFuelType.toLowerCase())) {
                // Try to set diesel as default, otherwise use first available
                if (availableFuelTypes.includes('diesel')) {
                    setSelectedFuelType('diesel');
                } else {
                    setSelectedFuelType(availableFuelTypes[0]);
                }
            }
        }
    }, [availableFuelTypes]);

    const { series, categories, legendData } = useMemo(() => {
        if (!infoConsumptions?.data || !Array.isArray(infoConsumptions.data))
            return { series: [], categories: [], legendData: [] };

        // Extract unique dates
        const dates = [...new Set(infoConsumptions.data.map(item => item.date))];

        // Extract unique establishments
        const establishmentsMap = new Map();
        infoConsumptions.data.forEach(item => {
            if (item.establishments && Array.isArray(item.establishments)) {
                item.establishments.forEach(est => {
                    if (!establishmentsMap.has(est.establishment_id)) {
                        establishmentsMap.set(
                            est.establishment_id,
                            est.establishment_name
                        );
                    }
                });
            }
        });

        const establishments = Array.from(establishmentsMap.entries());

        // Create series for each establishment, filtered by selected fuel type
        const seriesData = establishments.map(([estId, estName]) => ({
            name: estName,
            type: 'bar',
            stack: 'total',
            data: dates.map(date => {
                const dateData = infoConsumptions.data.find(
                    item => item.date === date
                );
                if (!dateData || !dateData.establishments) return 0;

                const estData = dateData.establishments.find(
                    e => e.establishment_id === estId
                );

                if (!estData || !estData.fuel_types) return 0;

                // Find the selected fuel type and return its consumption
                const fuelTypeData = estData.fuel_types.find(
                    fuel =>
                        fuel.name &&
                        fuel.name.toLowerCase() === selectedFuelType.toLowerCase()
                );

                return fuelTypeData ? Number(fuelTypeData.consumption) || 0 : 0;
            }),
        }));

        return {
            series: seriesData,
            categories: dates,
            legendData: establishments.map(([_, name]) => name),
        };
    }, [infoConsumptions?.data, selectedFuelType]);

    // Dummy data (50 centros)
    const legendDataDummy = Array.from({ length: 80 }, (_, i) => `Cargo ${i + 1}`);

    // Costos aleatorios entre 500k y 2M
    const costData = legendDataDummy.map(() =>
        Math.floor(Math.random() * (2000000 - 500000 + 1)) + 500000
    );

    // Cada centro es una serie independiente
    const seriesDummy = legendDataDummy.map((name, i) => ({
        name,
        type: 'bar',
        data: [costData[i]], // cada centro una barra
    }));

    const dataOptions = {
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                return (
                    '<b>Cargo:</b> ' + params.name + 
                    '<br/><b>Costo:</b> $' + params.data.toLocaleString('es-CL')
                );
            },
        },
        legend: {
            data: legendDataDummy,
            orient: 'horizontal',
            bottom: 10,
            type: 'scroll',
        },
        grid: {
            left: 60,
            right: 60,
            top: 60,
            bottom: 60,
        },
        xAxis: {
            type: 'category',
            data: legendDataDummy,
            axisLabel: { 
                rotate: 50,
                interval: 0,
                textStyle: { fontSize: 12 },
                margin: 15
            },
            axisTick: { show: true },
            axisLine: { show: true },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                formatter: (v) => '$' + v.toLocaleString('es-CL'),
            },
        },
        series: [
            {
                name: 'Costo',
                type: 'bar',
                data: costData,
                itemStyle: {
                    color: function (params) {
                        return ECHARTS_COLOR_PALETTE[2];
                    }
                }
            }
        ],
        dataZoom: [
            {
                type: 'slider',   // barra de scroll visible
                xAxisIndex: 0,    // se aplica al eje X
                start: 0,         // inicio del zoom (0%)
                end: 20         // fin del zoom (100%)
            },
            {
                type: 'inside',   // permite scroll con rueda del mouse o swipe
                xAxisIndex: 0,
                start: 0,
                end: 100
            }
        ]
    };

    const options = useMemo(
        () => (dataOptions),
        [series, categories, legendData]
    );

    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [hasValidData, setHasValidData] = useState(false);

    useEffect(() => {
        if (Array.isArray(infoConsumptions?.data)) {
            setHasLoadedOnce(true);
            if (infoConsumptions.data.length > 0) {
                setHasValidData(true);
            }
        }
    }, [infoConsumptions?.data]);

    // Only show empty state if we've loaded and confirmed there's no data
    const noData = hasLoadedOnce && !infoConsumptions?.data?.length;

    const actions = (
        <div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
            {/* <Select
                placeholder='Tipo de Combustible'
                data={availableFuelTypes.map(type => ({
                    label: type.charAt(0).toUpperCase() + type.slice(1),
                    value: type,
                }))}
                value={selectedFuelType}
                onChange={value => value && setSelectedFuelType(value)}
                size='xs'
                radius='lg'
                styles={{ input: { width: 150 } }}
                disabled={availableFuelTypes.length === 0}
            />
            <Select
                placeholder='Fecha'
                data={[
                    { label: 'Ciclo', value: 'ciclo' },
                    { label: 'Año', value: 'año' },
                    { label: 'Semestre', value: 'semestre' },
                    { label: 'Mes', value: 'mes' },
                    { label: 'Día', value: 'dia' },
                ]}
                value={selectedDate}
                onChange={value => value && setSelectedDate(value)}
                size='xs'
                radius='lg'
                styles={{ input: { width: 100 } }}
            /> */}
        </div>
    );

    return (
        <OChartCard
            title={title}
            actions={actions}
            isLoading={!hasLoadedOnce && !infoConsumptions?.data}
            error={null}
            isEmpty={noData}
            emptyIconClass='ri-gas-station-fill'
            emptyMessage='No cuenta con registro de consumo de combustible'
        >
            <OBarChart
                options={options}
                minHeight={minHeight}
                maxHeight={maxHeight}
            />
        </OChartCard>
    );
};

export default React.memo(CostPPEPositionEChart);
